import React, { useState } from 'react';
import { userService } from '../services/api';
import { Button, Input, Card } from './ui';
import AlertSystemDisclaimer from './AlertSystemDisclaimer';
import { Shield, User, Phone, Mail, Heart } from 'lucide-react';

/**
 * EmergencyContactSettings Component
 * 
 * Allows clients (especially those without clinician support) to add an emergency 
 * contact who can be notified when critical alerts occur.
 * 
 * Features:
 * - Add/edit emergency contact information
 * - Opt-in to send alerts to emergency contact
 * - Clear disclaimer about system limitations
 */
const EmergencyContactSettings = ({ user }) => {
  const [emergencyContact, setEmergencyContact] = useState({
    name: user.emergencyContact?.name || '',
    relationship: user.emergencyContact?.relationship || '',
    email: user.emergencyContact?.email || '',
    phone: user.emergencyContact?.phone || '',
    notifyOnAlerts: user.emergencyContact?.notifyOnAlerts || false
  });
  
  const [editing, setEditing] = useState(!user.emergencyContact?.name);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const hasClinicianSupport = !!user.assignedClinician;

  const handleChange = (field, value) => {
    setEmergencyContact(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      // Validate required fields
      if (!emergencyContact.name || !emergencyContact.relationship) {
        setMessage({
          type: 'error',
          text: 'Please provide at least a name and relationship for your emergency contact.'
        });
        setSaving(false);
        return;
      }

      if (!emergencyContact.email && !emergencyContact.phone) {
        setMessage({
          type: 'error',
          text: 'Please provide either an email or phone number for your emergency contact.'
        });
        setSaving(false);
        return;
      }

      await userService.updateProfile({
        emergencyContact: {
          ...emergencyContact,
          addedAt: user.emergencyContact?.addedAt || new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      });

      setMessage({
        type: 'success',
        text: 'Emergency contact saved successfully.'
      });
      setEditing(false);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to save emergency contact.'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset to saved values
    setEmergencyContact({
      name: user.emergencyContact?.name || '',
      relationship: user.emergencyContact?.relationship || '',
      email: user.emergencyContact?.email || '',
      phone: user.emergencyContact?.phone || '',
      notifyOnAlerts: user.emergencyContact?.notifyOnAlerts || false
    });
    setEditing(false);
    setMessage(null);
  };

  return (
    <div className="space-y-4">
      <AlertSystemDisclaimer variant="compact" />

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Shield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-semibold text-blue-900 mb-1">
              Universal Safety Feature — Available to All Clients
            </h4>
            <p className="text-sm text-blue-800">
              {hasClinicianSupport 
                ? 'Your assigned clinician receives critical alerts. Adding an emergency contact provides an additional safety layer—someone else you trust who can be notified if concerning wellness patterns are detected.'
                : 'This emergency contact feature ensures you have support even when managing your wellness journey independently. Your designated contact will receive notifications when critical alerts occur, providing a safety net for concerning wellness patterns.'
              }
            </p>
            <p className="text-sm text-blue-700 mt-2 font-medium">
              ✓ Works for all clients (clinician-guided or self-guided)<br />
              ✓ You control who is notified and when<br />
              ✓ Optional feature — configure only if desired
            </p>
          </div>
        </div>
      </div>

      {message && (
        <div
          className={`rounded-lg border p-3 text-sm ${
            message.type === 'success'
              ? 'border-green-300 bg-green-50 text-green-800'
              : 'border-red-300 bg-red-50 text-red-800'
          }`}
        >
          {message.text}
        </div>
      )}

      <Card>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                <User className="inline h-4 w-4 mr-1" />
                Full Name *
              </label>
              <Input
                type="text"
                value={emergencyContact.name}
                onChange={(e) => handleChange('name', e.target.value)}
                disabled={!editing}
                placeholder="e.g., Jane Smith"
                maxLength={100}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                <Heart className="inline h-4 w-4 mr-1" />
                Relationship *
              </label>
              <Input
                type="text"
                value={emergencyContact.relationship}
                onChange={(e) => handleChange('relationship', e.target.value)}
                disabled={!editing}
                placeholder="e.g., Spouse, Parent, Friend"
                maxLength={50}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                <Mail className="inline h-4 w-4 mr-1" />
                Email Address
              </label>
              <Input
                type="email"
                value={emergencyContact.email}
                onChange={(e) => handleChange('email', e.target.value)}
                disabled={!editing}
                placeholder="contact@example.com"
              />
              <p className="text-xs text-neutral-500 mt-1">Alerts will be sent to this email</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                <Phone className="inline h-4 w-4 mr-1" />
                Phone Number
              </label>
              <Input
                type="tel"
                value={emergencyContact.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                disabled={!editing}
                placeholder="(555) 123-4567"
              />
              <p className="text-xs text-neutral-500 mt-1">For future SMS alert support</p>
            </div>
          </div>

          <div className="border-t border-neutral-200 pt-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={emergencyContact.notifyOnAlerts}
                onChange={(e) => handleChange('notifyOnAlerts', e.target.checked)}
                disabled={!editing}
                className="mt-1"
              />
              <div className="flex-1">
                <span className="block font-medium text-neutral-800">
                  Send critical alerts to this emergency contact
                </span>
                <span className="block text-sm text-neutral-600 mt-1">
                  When enabled, this person will receive notifications for critical safety alerts 
                  (e.g., high-risk mood patterns, consecutive concerning check-ins). They will NOT 
                  see your journal entries, daily check-in details, or other private content.
                </span>
              </div>
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            {editing ? (
              <>
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Emergency Contact'}
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                onClick={() => setEditing(true)}
              >
                Edit Emergency Contact
              </Button>
            )}
          </div>
        </div>
      </Card>

      <div className="text-xs text-neutral-500 italic">
        * Your emergency contact information is stored securely and will only be used to send 
        critical alerts as configured above. You can update or remove this information at any time.
      </div>
    </div>
  );
};

export default EmergencyContactSettings;
